import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { MenuCardScrollContainer } from "../../../menu/MenuCardScrollContainer";
import { ListItem } from "../../../menu/ListItem";
import { AiFillSound, AiOutlineSearch } from "react-icons/ai";
import { InputWithMargin } from "../../../editor/Input";
import Highlighter from "react-highlight-words";
import { BsBraces } from "react-icons/bs";
import { SoundActionHelper } from "../../../../canvas/game/controller/SoundActionHelper";
import { UiConstants } from "../../../../data/UiConstants";
const Container = styled.div.withConfig({
  displayName: "SoundSelectionDetail__Container",
  componentId: "sc-56i2jw-0"
})(["display:flex;flex-direction:column;min-width:350px;"]);
const CenterRow = styled.div.withConfig({
  displayName: "SoundSelectionDetail__CenterRow",
  componentId: "sc-56i2jw-1"
})(["display:flex;flex-direction:row;justify-content:center;"]);
const Audio = styled.audio.withConfig({
  displayName: "SoundSelectionDetail__Audio",
  componentId: "sc-56i2jw-2"
})(["margin:4px;"]);
const SoundIcon = styled(AiFillSound).withConfig({
  displayName: "SoundSelectionDetail__SoundIcon",
  componentId: "sc-56i2jw-3"
})(["padding-top:4px;margin-right:4px;"]);
const TreeParameterIcon = styled(BsBraces).withConfig({
  displayName: "SoundSelectionDetail__TreeParameterIcon",
  componentId: "sc-56i2jw-4"
})(["padding-top:4px;margin-right:4px;"]);
const Headline = styled.div.withConfig({
  displayName: "SoundSelectionDetail__Headline",
  componentId: "sc-56i2jw-5"
})([""]);
const ScrollContainer = styled(MenuCardScrollContainer).withConfig({
  displayName: "SoundSelectionDetail__ScrollContainer",
  componentId: "sc-56i2jw-6"
})(["max-height:150px;margin:4px;&.invalid{outline:2px solid ", ";border-radius:2px;border-color:rgba(0,0,0,0);}&:focus{outline:2px solid ", ";border-radius:2px;border-color:rgba(0,0,0,0);}&:focus.invalid{outline:2px solid ", ";border-radius:2px;border-color:rgba(0,0,0,0);}"], UiConstants.COLOR_VALUE_INVALID, UiConstants.COLOR_SELECTION_HIGHLIGHT, UiConstants.COLOR_VALUE_INVALID);
const SearchRow = styled.div.withConfig({
  displayName: "SoundSelectionDetail__SearchRow",
  componentId: "sc-56i2jw-7"
})(["display:flex;flex-direction:row;justify-content:center;align-items:center;"]);
const NoSoundText = styled.div.withConfig({
  displayName: "SoundSelectionDetail__NoSoundText",
  componentId: "sc-56i2jw-8"
})(["margin:4px;"]);
const Separator = styled.hr.withConfig({
  displayName: "SoundSelectionDetail__Separator",
  componentId: "sc-56i2jw-9"
})(["border:0;border-top:1px dashed grey;"]);
const SearchIcon = styled(AiOutlineSearch).withConfig({
  displayName: "SoundSelectionDetail__SearchIcon",
  componentId: "sc-56i2jw-10"
})(["display:flex;flex-grow:0;flex-shrink:1;width:20px;"]);
const SearchInput = styled(InputWithMargin).withConfig({
  displayName: "SoundSelectionDetail__SearchInput",
  componentId: "sc-56i2jw-11"
})(["flex-grow:1;flex-shrink:0;"]);
export const SoundSelectionDetail = observer(props => {
  const {
    t
  } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [treeParameterSounds] = useState(SoundActionHelper.findSoundParameterNames(props.action));
  const soundUrl = props.selectedPath ? props.selectedPath.dir + "/" + props.selectedPath.name + props.selectedPath.ext : null;
  const currentSoundList = filterBySearchText(props.soundPaths);
  const areSoundsAvailable = currentSoundList.length > 0;

  function onSelectPath(path) {
    if (path == props.selectedPath) props.onSelectPath(null);else props.onSelectPath(path);
  }

  function onSelectTreeParameter(treeParameter) {
    props.onSelectTreeParameter(treeParameter);
  }

  function onSearchTextChange(text) {
    setSearchText(text);
  }

  function getSelectionTitle() {
    if (props !== null && props !== void 0 && props.selectedPath) return props === null || props === void 0 ? void 0 : props.selectedPath.name;
    if (props !== null && props !== void 0 && props.selectedTreeParameter) return props === null || props === void 0 ? void 0 : props.selectedTreeParameter;
    return "-";
  }

  function filterBySearchText(paths) {
    if (!paths) return [];
    if (!searchText || searchText.trim().length == 0) return paths;
    return paths.filter(path => path.name && path.name.toLowerCase().includes(searchText)).filter(item => item != null);
  }

  return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Headline, null, t("action_editor.node_play_sound_select") + ": " + getSelectionTitle()), /*#__PURE__*/React.createElement(SearchRow, null, /*#__PURE__*/React.createElement(SearchIcon, null), /*#__PURE__*/React.createElement(SearchInput, {
    type: "text",
    onChange: ({
      target
    }) => onSearchTextChange(target.value)
  }))), areSoundsAvailable && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ScrollContainer, {
    className: !props.showEmptyStateAsInvalid || props.selectedPath || props.selectedTreeParameter ? "" : "invalid"
  }, // == parameter sound list ==
  treeParameterSounds.map((soundParameterName, index) => /*#__PURE__*/React.createElement(ListItem, {
    key: index,
    className: soundParameterName == props.selectedTreeParameter ? "selected" : "",
    onClick: _ => onSelectTreeParameter(soundParameterName)
  }, /*#__PURE__*/React.createElement(TreeParameterIcon, null), soundParameterName)), treeParameterSounds.length > 0 && /*#__PURE__*/React.createElement(Separator, null), // == default sound list ==
  currentSoundList.map((path, index) => {
    var _props$selectedPath, _props$selectedPath2;

    return /*#__PURE__*/React.createElement(ListItem, {
      key: index,
      className: (path === null || path === void 0 ? void 0 : path.dir) == ((_props$selectedPath = props.selectedPath) === null || _props$selectedPath === void 0 ? void 0 : _props$selectedPath.dir) && (path === null || path === void 0 ? void 0 : path.base) == ((_props$selectedPath2 = props.selectedPath) === null || _props$selectedPath2 === void 0 ? void 0 : _props$selectedPath2.base) ? "selected" : "",
      onClick: _ => onSelectPath(path)
    }, (path === null || path === void 0 ? void 0 : path.name) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SoundIcon, null), /*#__PURE__*/React.createElement(Highlighter, {
      searchWords: [searchText],
      textToHighlight: path.name
    })));
  })), /*#__PURE__*/React.createElement(CenterRow, null, // == sound preview ==
  soundUrl && /*#__PURE__*/React.createElement(Audio, {
    controls: true,
    preload: "auto",
    src: soundUrl
  }))), !areSoundsAvailable && /*#__PURE__*/React.createElement(NoSoundText, null, t("action_editor.node_play_sound_no_sounds")));
});